import { ProductFeaturesProps } from "@omni/types/components/props/productFeatures.props";
import { cn } from "@omni/utils/styles";
import { useState } from "react";

import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { InfoBlockAnchor } from "../../components/infoBlockAnchor.component";
import { InfoBlockContent } from "../../components/infoBlockContent.component";
import { InfoBlockHeading } from "../../components/infoBlockHeading.component";
import { InfoBlockWrapper } from "../../components/infoBlockWrapper.component";
import {
    getFeaturesGroupedPerKind,
    getSortedFeatureKinds
} from "../../helpers";
import { FavvCode } from "./favvCode/favvCode.component";
import { FeatureLabels } from "./featureLabels/featureLabels.component";
import { FeatureSection } from "./featureSection/featureSection.component";
import { SafetyDocuments } from "./safetyDocuments/safetyDocuments.component";

const FAVVCODE_OTHERS = "ANDERE";

const FeatureList = ({ product, maxVisibleFeatures }: ProductFeaturesProps) => {
    const { t } = useGranite();
    const [showMobileMore, setShowMobileMore] = useState(false);

    const sortedFeatureKinds = getSortedFeatureKinds(product, t);
    const featuresGroupedPerKind = getFeaturesGroupedPerKind(product, t);

    const displayFavvCodeBlock =
        product.favvCode != null && !product.favvCode.includes(FAVVCODE_OTHERS);

    const isReadMoreDataAvailable =
        product.precautionaryStatements.length > 0 ||
        !!product.sdsUrl ||
        !!product.dopUrl;

    const safetyInfoAvailable =
        !!product.signalword ||
        product.safetyIcons.length > 0 ||
        product.hazardStatements.length > 0 ||
        isReadMoreDataAvailable;

    return (
        <InfoBlockWrapper showMobileMore={showMobileMore}>
            <InfoBlockHeading
                title={t("productDetail.anchor.title.features")}
                onClick={() => setShowMobileMore((state) => !state)}
            />
            <InfoBlockContent>
                <Row>
                    <Col>
                        <InfoBlockAnchor
                            id={t("productDetail.anchor.hash.features")}
                        />
                        <h4
                            className={cn(
                                "tw:mb-4 tw:font-bold anchor--pdp-info-nav"
                            )}
                        >
                            <span>
                                {t("productDetail.anchor.title.features")}
                            </span>
                        </h4>

                        {sortedFeatureKinds.map((featureKind) => {
                            return (
                                <FeatureSection
                                    key={`featureKind-${featureKind.code}`}
                                    featureKind={featureKind}
                                    maxVisibleFeatures={maxVisibleFeatures}
                                    featuresGroupedPerKind={
                                        featuresGroupedPerKind
                                    }
                                />
                            );
                        })}
                        {displayFavvCodeBlock && (
                            <FavvCode code={product.favvCode} />
                        )}
                        {safetyInfoAvailable && (
                            <SafetyDocuments
                                signalWord={product.signalword}
                                hazardStatements={product.hazardStatements.join(
                                    " "
                                )}
                                dopUrl={product.dopUrl}
                                sdsUrl={product.sdsUrl}
                                precautionaryStatements={product.precautionaryStatements.join(
                                    " "
                                )}
                                safetyIcons={product.safetyIcons}
                                isReadMoreDataAvailable={
                                    isReadMoreDataAvailable
                                }
                            />
                        )}
                        <FeatureLabels product={product} />
                    </Col>
                </Row>
            </InfoBlockContent>
        </InfoBlockWrapper>
    );
};

export { FeatureList };
